<!-- eslint-disable no-undef -->
<script setup>
  // Imports
  import { ref, onMounted, computed, watch } from 'vue';
  // import { useStore } from 'vuex';
  import LoadingAnimation3 from '../loadingIcons/LoadingAnimation3.vue';
  import InfoIcon from '@/components/common/InfoIcon.vue';
  import Roles from '@/api/roles';
  import { useModalBackdropClose } from '@/composables/useModalBackdropClose';

  // Definitions
  const props = defineProps({
    roleDetails: {
      type: Object,
      default: () => {}
    }
  });

  // const store = useStore();
  // const user = computed(() => store.state.user);

  const emit = defineEmits(['close', 'role-created', 'role-updated']);

  const loading = ref(false);
  const loadingMessage = ref('');
  const loadingPermissions = ref(true);
  const error = ref('');

  // Form fields
  const roleName = ref(
    props.roleDetails?.id
      ? props.roleDetails.id.replace(/-/g, ' ')
      : ''
  );
  const roleDescription = ref(props.roleDetails?.description || '');
  const baseRole = ref(props.roleDetails?.baseRole || '');

  // Role and permission data
  const existingRoles = ref([]);
  const availablePermissions = ref([]);
  const selectedPermissions = ref([]);

  // Permissions table UI state
  const searchQuery = ref('');
  const activeCategory = ref('all');

  const categories = computed(() => {
    const cats = new Set(availablePermissions.value.map(p => p.category).filter(Boolean));
    return [...cats].sort();
  });

  const filteredPermissions = computed(() => {
    const q = searchQuery.value.trim().toLowerCase();
    if (q) {
      return availablePermissions.value.filter(p =>
        p.id?.toLowerCase().includes(q) ||
        p.name?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q) ||
        p.category?.toLowerCase().includes(q)
      );
    }
    return availablePermissions.value.filter(p => p.category === activeCategory.value);
  });

  const allFilteredSelected = computed(() =>
    filteredPermissions.value.length > 0 &&
    filteredPermissions.value.every(p => selectedPermissions.value.includes(p.id))
  );

  const someFilteredSelected = computed(() =>
    filteredPermissions.value.some(p => selectedPermissions.value.includes(p.id)) &&
    !allFilteredSelected.value
  );

  const categoryCount = (cat) => availablePermissions.value.filter(p => p.category === cat && selectedPermissions.value.includes(p.id)).length;

  const onTabsWheel = (e) => {
    if (e.deltaY !== 0) {
      e.preventDefault();
      e.currentTarget.scrollBy({ left: e.deltaY, behavior: 'smooth' });
    }
  };

  const setCategory = (cat) => {
    activeCategory.value = cat;
    searchQuery.value = '';
  };

  const togglePermission = (id) => {
    const idx = selectedPermissions.value.indexOf(id);
    if (idx === -1) selectedPermissions.value.push(id);
    else selectedPermissions.value.splice(idx, 1);
  };

  const toggleSelectAll = () => {
    if (allFilteredSelected.value) {
      selectedPermissions.value = selectedPermissions.value.filter(
        id => !filteredPermissions.value.some(p => p.id === id)
      );
    } else {
      const toAdd = filteredPermissions.value
        .filter(p => !selectedPermissions.value.includes(p.id))
        .map(p => p.id);
      selectedPermissions.value.push(...toAdd);
    }
  };

  const isEditMode = computed(() => Object.keys(props.roleDetails || {}).length > 0);

  // Lifecycle hooks
  onMounted(async () => {
    await Promise.all([
      loadExistingRoles(),
      loadAvailablePermissions()
    ]);
    
    // If editing, pre-select permissions
    if (isEditMode.value) {
      await loadRolePermissions();
    }
    
    loadingPermissions.value = false;
    if (categories.value.length > 0) {
      activeCategory.value = categories.value[0];
    }
  });

  // Watch for base role changes to pre-select permissions
  watch(baseRole, async (newBaseRole, oldBaseRole) => {
    // Only load permissions if we're not in edit mode and a base role is selected
    if (!isEditMode.value && newBaseRole) {
      await loadBaseRolePermissions(newBaseRole);
    } else if (!newBaseRole && oldBaseRole) {
      // Clear selections if base role was cleared (but preserve if editing)
      if (!isEditMode.value) {
        selectedPermissions.value = [];
      }
    }
  });

  // Methods
  const loadExistingRoles = async () => {
    try {
      // TODO: Replace with actual API call
      const response = await Roles.get('');
      existingRoles.value = response.roles;
      
    } catch (error) {
      console.error('Error loading existing roles:', error);
      error.value = 'Failed to load existing roles';
    }
  };

  const loadAvailablePermissions = async () => {
    try {
      const response = await Roles.get('/options');
      availablePermissions.value = response.permissions;
      
      // Rearrange order: id, category, name, description, then remaining keys
      if (Array.isArray(availablePermissions.value)) {
        availablePermissions.value = availablePermissions.value.map(perm => {
          // Create a new object with explicit key order
          const reordered = {};
          
          // Add keys in the desired order
          if ('id' in perm) reordered.id = perm.id;
          if ('category' in perm) reordered.category = perm.category;
          if ('name' in perm) reordered.name = perm.name;
          if ('description' in perm) reordered.description = perm.description;
          
          // Add remaining keys
          Object.keys(perm).forEach(key => {
            if (!['id', 'category', 'name', 'description'].includes(key)) {
              reordered[key] = perm[key];
            }
          });
          
          return reordered;
        });
      }

    } catch (error) {
      console.error('Error loading available permissions:', error);
      error.value = 'Failed to load available permissions';
    }
  };

  const loadBaseRolePermissions = async (roleId) => {
    try {
      const role = existingRoles.value.find(r => r.id === roleId);
      if (!role || !role.permissions) {
        selectedPermissions.value = [];
        return;
      }
      selectedPermissions.value = [...role.permissions];
    } catch (err) {
      console.error('Error loading base role permissions:', err);
    }
  };

  const loadRolePermissions = async () => {
    try {
      if (props.roleDetails.permissions && Array.isArray(props.roleDetails.permissions)) {
        selectedPermissions.value = [...props.roleDetails.permissions];
      }
    } catch (err) {
      console.error('Error loading role permissions:', err);
    }
  };

  const validateForm = () => {
    error.value = '';
    
    if (!isEditMode.value && (!roleName.value || roleName.value.trim() === '')) {
      error.value = 'Role name is required';
      return false;
    }
    
    if (selectedPermissions.value.length === 0) {
      error.value = 'At least one permission must be selected';
      return false;
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    loading.value = true;
    loadingMessage.value = isEditMode.value ? 'Updating Role...' : 'Creating Role...';
    error.value = '';

    try {
      const roleData = {
        name: roleName.value.trim(),
        description: roleDescription.value.trim(),
        permissions: selectedPermissions.value
      };

      if (isEditMode.value) {
        const response = await Roles.put(`/${props.roleDetails.id}`, roleData);
        
        // Check if the response indicates success (either status field or no error)
        if (response.status === 'success' || response.status === 200 || !response.error) {
          emit('role-updated', response.details || { id: props.roleDetails.id, ...roleData });
          close();
        } else {
          error.value = response.message || response.error || 'Failed to update role';
        }
      } else {
        const response = await Roles.post('', roleData);
        
        // Check if the response indicates success (either status field or no error)
        if (response.status === 'success' || response.status === 200 || !response.error) {
          emit('role-created', response.details || { id: response.id || 'new-role-id', ...roleData });
          close();
        } else {
          error.value = response.message || response.error || 'Failed to create role';
        }
      }
    } catch (error) {
      console.error('Error saving role:', error);
      error.value = 'An error occurred while saving the role';
    } finally {
      loading.value = false;
    }
  };

  const close = () => {
    loading.value = false;
    error.value = '';
    emit('close');
  };

  const { onBackdropMousedown, onBackdropMouseup, onContentMousedown } = useModalBackdropClose(close);
</script>

<template>
  <div class="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex justify-center items-center z-50" @mousedown.self="onBackdropMousedown" @mouseup.self="onBackdropMouseup">
    <div class="ui-modal p-6 rounded-lg w-[800px] max-h-[calc(100vh-50px)] overflow-y-auto shadow-2xl" @mousedown="onContentMousedown">
      <!-- Header -->
      <div class="mb-6">
        <div class="h-[3px] w-full bg-mc-red rounded-full mb-5"></div>
        <h2 v-if="!isEditMode" class="font-extrabold text-xl text-center mb-5 text-mc-ink dark:text-on-dark tracking-tight">Create Custom Role</h2>
        <h2 v-else class="font-extrabold text-xl text-center mb-5 text-mc-ink dark:text-on-dark tracking-tight">{{ roleName }}</h2>
      </div>

      <form @submit.prevent="handleSubmit" class="space-y-6">
        <!-- Build off existing role -->
        <div v-if="!isEditMode">
          <label for="base-role" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">
            Build off Existing Role (Optional)
            <InfoIcon
              class="ml-2"
              iconType="info"
              content="Select an existing role to pre-populate permissions. You can then add or remove permissions as needed."
            />
          </label>
          <select
            id="base-role"
            v-model="baseRole"
            class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
            :disabled="isEditMode"
          >
            <option value="">-- None (Start from scratch) --</option>
            <option
              v-for="role in existingRoles"
              :key="role.id"
              :value="role.id"
            >
              {{ role.id.replace(/-/g, ' ') }}
            </option>
          </select>
        </div>

        <!-- Role Name -->
        <div v-if="!isEditMode">
          <label for="role-name" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">Role Name *</label>
          <input
            id="role-name"
            v-model="roleName"
            type="text"
            class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
            placeholder="e.g., Custom Manager"
          />
        </div>

        <!-- Role Description -->
        <div>
          <label for="role-description" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 block">Description</label>
          <textarea
            id="role-description"
            v-model="roleDescription"
            rows="3"
            class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
            placeholder="Describe what this role is used for..."
          ></textarea>
        </div>

        <!-- Permissions Table -->
        <div class="bg-mc-cloud dark:bg-black/20 border border-mc-steel dark:border-line-on-dark rounded-mc-sm overflow-hidden">
          <div class="p-4 pb-3">
            <span class="font-bold">
              Permissions: *
              <InfoIcon
                class="ml-2"
                iconType="info"
                content="Select the permissions this role should have. You can select multiple permissions."
              />
            </span>
          </div>

          <!-- Loading state -->
          <div v-if="loadingPermissions" class="flex items-center justify-center py-10">
            <LoadingAnimation3 size="30px" message="Loading permissions..." />
          </div>

          <template v-else>
            <!-- Category tabs -->
            <div class="flex overflow-x-auto border-y border-mc-steel dark:border-line-on-dark bg-mc-cloud dark:bg-black/20" @wheel="onTabsWheel">
              <button
                v-for="cat in categories"
                :key="cat"
                type="button"
                @click="setCategory(cat)"
                class="flex-shrink-0 flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors duration-160 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-red/20"
                :class="activeCategory === cat && !searchQuery.trim()
                  ? 'border-mc-red text-mc-ink dark:text-on-dark'
                  : 'border-transparent text-ink-muted dark:text-on-dark-muted hover:text-mc-ink dark:hover:text-on-dark'"
              >
                {{ cat.charAt(0).toUpperCase() + cat.slice(1) }}
                <span v-if="categoryCount(cat) > 0" class="text-xs rounded-full px-1.5 py-0.5 bg-mc-steel/30 dark:bg-white/10 font-mono">
                  {{ categoryCount(cat) }}
                </span>
              </button>
            </div>

            <!-- Search bar -->
            <div class="p-2 border-b border-mc-steel dark:border-line-on-dark">
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search permissions…"
                class="w-full py-1.5 px-3 text-sm border rounded-mc-sm border-mc-steel dark:border-line-on-dark bg-mc-cloud dark:bg-mc-charcoal text-mc-ink dark:text-on-dark placeholder:text-ink-muted dark:placeholder:text-on-dark-muted focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-mc-red/20 focus-visible:border-mc-red transition-colors duration-160"
              />
            </div>

            <!-- Table -->
            <div class="overflow-x-auto overflow-y-auto h-[280px]">
              <table class="w-full border-collapse whitespace-nowrap text-sm">
                <thead>
                  <tr>
                    <th class="bg-mc-ink dark:bg-black/50 px-3 py-2 w-10">
                      <input
                        type="checkbox"
                        :checked="allFilteredSelected"
                        :indeterminate="someFilteredSelected"
                        @change="toggleSelectAll"
                        class="w-4 h-4 cursor-pointer accent-mc-red"
                      />
                    </th>
                    <th class="bg-mc-ink dark:bg-black/50 px-3 py-2 text-left font-mono text-xs uppercase tracking-[0.08em] text-on-dark">Name</th>
                    <th v-if="searchQuery.trim()" class="bg-mc-ink dark:bg-black/50 px-3 py-2 text-left font-mono text-xs uppercase tracking-[0.08em] text-on-dark">Category</th>
                    <th class="bg-mc-ink dark:bg-black/50 px-3 py-2 text-left font-mono text-xs uppercase tracking-[0.08em] text-on-dark">Description</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-mc-steel/40 dark:divide-line-on-dark">
                  <tr
                    v-for="perm in filteredPermissions"
                    :key="perm.id"
                    class="hover:bg-mc-blue/5 dark:hover:bg-white/5 transition-colors duration-120 cursor-pointer"
                    @click="togglePermission(perm.id)"
                  >
                    <td class="px-3 py-2 w-10" @click.stop>
                      <input
                        type="checkbox"
                        :checked="selectedPermissions.includes(perm.id)"
                        @change="togglePermission(perm.id)"
                        class="w-4 h-4 cursor-pointer accent-mc-red"
                      />
                    </td>
                    <td class="px-3 py-2 text-mc-ink dark:text-on-dark font-medium">{{ perm.name || perm.id }}</td>
                    <td v-if="searchQuery.trim()" class="px-3 py-2">
                      <span class="inline-block bg-mc-steel/20 dark:bg-white/10 rounded-full px-2 py-0.5 text-xs text-ink-muted dark:text-on-dark-muted">{{ perm.category }}</span>
                    </td>
                    <td class="px-3 py-2 text-ink-muted dark:text-on-dark-muted max-w-xs">
                      <div class="truncate" :title="perm.description">{{ perm.description || '--' }}</div>
                    </td>
                  </tr>
                  <tr v-if="filteredPermissions.length === 0">
                    <td :colspan="searchQuery.trim() ? 4 : 3" class="px-4 py-8 text-center text-sm text-ink-muted dark:text-on-dark-muted">
                      No permissions found
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Footer count -->
            <div class="px-4 py-2.5 border-t border-mc-steel dark:border-line-on-dark text-sm text-ink-muted dark:text-on-dark-muted">
              Selected: <span class="font-semibold text-mc-ink dark:text-on-dark">{{ selectedPermissions.length }}</span> permission(s)
            </div>
          </template>
        </div>

        <!-- Error Messages -->
        <div v-if="error" class="mt-5">
          <p class="font-normal text-red-600 dark:text-red-400 whitespace-pre-line bg-red-50 dark:bg-red-900/30 p-3 rounded-lg">
            {{ error }}
          </p>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-between items-center mt-8 pt-6 border-t border-mc-steel dark:border-line-on-dark">
          <button
            type="button"
            @click="close"
            class="px-5 py-2 text-xs rounded-full border border-mc-steel dark:border-white/20 text-ink-muted dark:text-on-dark-muted hover:bg-mc-steel/20 dark:hover:bg-white/10 transition-colors duration-160"
            :disabled="loading"
          >
            Cancel
          </button>
          
          <div class="flex items-center gap-3">
            <LoadingAnimation3 
              v-if="loading" 
              size="30px"
              :message="loadingMessage"
            />
            <button 
              v-else
              type="submit" 
              :disabled="loading || (!isEditMode && !roleName.trim()) || selectedPermissions.length === 0" 
              class="default-button" 
              :class="{'default-button-off': loading || (!isEditMode && !roleName.trim()) || selectedPermissions.length === 0}"
            >
              {{ isEditMode ? 'Update' : 'Create' }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>
